import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/errors";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Loader2, X } from "lucide-react";

const $ = (v) => (v === null || v === undefined ? "—" : `$${Number(v).toFixed(2)}`);

// V5.1 §8 Founder cancellation review: live refund quote with actual fees, waivers, override, approval, audit history.
export function CancellationReviewPanel({ order, onDone }) {
  const [quote, setQuote] = useState(null);
  const [waiveFees, setWaiveFees] = useState(false);
  const [waiveCharge, setWaiveCharge] = useState(false);
  const [override, setOverride] = useState("");
  const [reason, setReason] = useState("");
  const [ack, setAck] = useState(false);
  const [saving, setSaving] = useState(false);
  const isSupply = !!order.direct && order.fulfillment_mode !== "FULL_CONTAINER";
  const request = order.direct?.cancellation_request || order.cancellation_request;
  const active = ["PAID", "CONFIRMED", "BALANCE_DUE"].includes(order.status) && (isSupply || !order.direct);
  const terminal = ["CANCELLED", "REFUND_INITIATED", "REFUND_FAILED"].includes(order.status) && order.cancellation_quote;
  const [adminOpened, setAdminOpened] = useState(false);
  const hasRequest = request?.status === "PENDING";
  const visible = terminal || (active && (hasRequest || adminOpened));  // RC52-ORD-001: panel only via dealer request or explicit admin action

  const loadQuote = useCallback(() => {
    if (!active || !(hasRequest || adminOpened)) return;
    api.get(`/admin/orders/${order.id}/cancellation-quote?waive_fees=${waiveFees}&waive_charge=${waiveCharge}`)
      .then((r) => setQuote(r.data)).catch(() => setQuote(null));
  }, [order.id, waiveFees, waiveCharge, active, hasRequest, adminOpened]);
  useEffect(loadQuote, [loadQuote]);

  if (!visible) {
    if (!active) return null;
    return (
      <div className="mt-6 flex justify-end print:hidden">
        <Button size="sm" variant="outline" onClick={() => setAdminOpened(true)} data-testid="admin-open-cancellation" className="border-limestone text-charcoal hover:bg-sand">Cancel order (admin action)…</Button>
      </div>
    );
  }
  const q = terminal ? order.cancellation_quote : quote;
  const fees = order.payment_fees || [];
  const refundPreview = override !== "" ? Number(override) : q?.refund_usd;

  const submit = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(`/admin/orders/${order.id}/cancel-with-quote`, {
        reason, waive_processing_fees: waiveFees, waive_cancellation_charge: waiveCharge,
        refund_override_usd: override === "" ? null : Number(override), exception_ack: ack,
      });
      const res = data.refund_result;
      if (res?.outcome === "FAILED") toast.error(`Cancellation approved — ${res.message}`);
      else toast.success(res ? `Cancellation approved — ${res.message}` : "Cancellation approved — nothing to refund");
      onDone?.();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Card className="mt-6 border-border bg-white" data-testid="admin-cancellation-panel">
      <CardContent className="p-5">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-charcoal">Cancellation review</p>
          {request?.status && <span className="badge-status badge-reserved" data-testid="admin-cancel-request-status">Dealer request · {request.status}</span>}
        </div>
        {request?.reason && <p className="mt-1 text-xs text-warmgrey" data-testid="admin-cancel-request-reason">{request.reason}</p>}

        <div className="mt-3 grid gap-4 md:grid-cols-2">
          <div className="rounded-md border border-border bg-background p-3 text-xs" data-testid="admin-fee-table">
            <p className="font-semibold text-charcoal">Payments & actual processing fees</p>
            {fees.length === 0 ? <p className="mt-1 text-warmgrey">No settled payments.</p> : fees.map((p) => (
              <div key={p.phase} className="mt-1 flex justify-between" data-testid={`admin-fee-${p.phase}`}>
                <span className="text-warmgrey">{p.phase} · {p.method || "method n/a"}</span>
                <span className="num text-charcoal">{$(p.amount_usd)} · fee {p.fee_usd === null || p.fee_usd === undefined ? <span className="text-status-sold">unknown</span> : $(p.fee_usd)}</span>
              </div>
            ))}
          </div>
          <div className="rounded-md border border-border bg-background p-3 text-xs" data-testid="admin-refund-quote">
            <p className="font-semibold text-charcoal">Refund calculation{q?.stage ? ` · stage ${q.stage}` : ""}</p>
            {!q ? <p className="mt-1 text-warmgrey">Loading…</p> : (
              <>
                <div className="flex justify-between"><span className="text-warmgrey">Amount paid</span><span className="num" data-testid="admin-quote-paid">{$(q.paid_total_usd)}</span></div>
                <div className="flex justify-between"><span className="text-warmgrey">Non-recoverable fees{q.waived_fees ? " (waived)" : ""}</span><span className="num" data-testid="admin-quote-fees">{q.fee_unknown && !q.waived_fees ? "unknown" : `− ${$(q.waived_fees ? 0 : q.fees_total_usd)}`}</span></div>
                {(q.charge_usd > 0 || q.waived_charge) && <div className="flex justify-between"><span className="text-warmgrey">Cancellation charge {Math.round(q.charge_pct * 100)}%{q.waived_charge ? " (waived)" : ""}</span><span className="num" data-testid="admin-quote-charge">− {$(q.waived_charge ? 0 : q.charge_usd)}</span></div>}
                <div className="mt-1 flex justify-between border-t border-border pt-1 font-semibold"><span>Refund</span><span className="num" data-testid="admin-quote-refund">{q.requires_admin_review ? "needs decision" : $(q.refund_usd)}</span></div>
                <ul className="mt-1 list-disc pl-4 text-warmgrey" data-testid="admin-quote-reasons">{(q.reasons || []).map((r, i) => <li key={i}>{r}</li>)}</ul>
              </>
            )}
          </div>
        </div>

        {terminal ? (
          <div className="mt-3 text-xs text-warmgrey" data-testid="admin-cancel-history">
            Cancelled {order.cancelled_at ? new Date(order.cancelled_at).toLocaleString() : ""} · refund {$(order.refund?.confirmed_total_usd ?? order.refund?.total_usd)} · {order.refund?.status === "SUCCEEDED" ? "confirmed by payment provider" : order.refund?.status === "FAILED" ? "failed" : "pending provider confirmation"}
            {order.founder_override && ` · Founder override by ${order.founder_override.by}: fees waived ${order.founder_override.waive_processing_fees ? "yes" : "no"}, 5% waived ${order.founder_override.waive_cancellation_charge ? "yes" : "no"}${order.founder_override.refund_override_usd != null ? `, refund override ${$(order.founder_override.refund_override_usd)}` : ""}`}
            {(order.refund?.items || []).map((it) => <span key={it.stripe_refund_id} className="block font-mono">{it.phase}: {it.stripe_refund_id} · {$(it.amount_usd)} · {it.status}</span>)}
          </div>
        ) : (
          <div className="mt-4 space-y-2 text-xs">
            <div className="flex flex-wrap gap-4">
              <label className="flex items-center gap-2 text-charcoal"><input type="checkbox" checked={waiveFees} onChange={(e) => setWaiveFees(e.target.checked)} className="accent-[#B86F54]" data-testid="admin-waive-fees" /> Waive processing fees</label>
              {(q?.charge_usd > 0 || q?.waived_charge) && <label className="flex items-center gap-2 text-charcoal"><input type="checkbox" checked={waiveCharge} onChange={(e) => setWaiveCharge(e.target.checked)} className="accent-[#B86F54]" data-testid="admin-waive-charge" /> Waive 5% cancellation charge</label>}
              {q?.final_sale && <label className="flex items-center gap-2 text-status-sold"><input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} className="accent-[#B86F54]" data-testid="admin-exception-ack" /> Final-sale exception</label>}
            </div>
            <div className="flex flex-wrap gap-2">
              <Input type="number" min="0" step="0.01" value={override} onChange={(e) => setOverride(e.target.value)} placeholder={`Refund override (default ${$(q?.refund_usd)})`} className="h-9 w-56 bg-white" data-testid="admin-refund-override" />
              <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (required, audited)" className="h-9 flex-1 bg-white" data-testid="admin-cancel-reason" />
              <Button size="sm" onClick={submit} disabled={saving || reason.trim().length < 3 || !q || (!q.cancellable && !ack)} data-testid="admin-cancel-approve" className="bg-status-sold text-white hover:opacity-90">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><X className="mr-1 h-3.5 w-3.5" /> Approve cancellation · refund {$(refundPreview)}</>}
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
